/**
 * Pure, unit-tested filter + form-validation logic behind the complaints list
 * and new-complaint form. Kept out of the page components so the predicate,
 * distinct-company math and validation rules are testable in isolation
 * (mirrors the offersFilter.ts idiom).
 *
 * Client-side only: the full complaints list the caller is entitled to see is
 * already loaded by the page — visibility narrowing (own company / assigned
 * customers / all) happens server-side, this module only slices the already-
 * scoped list further by the filter bar's selections.
 */
import type { Complaint, ComplaintKind, ComplaintStatus } from "../api/complaints";

/** Slice of Complaint the filter predicate + distinct-company helper actually need. */
export type FilterableComplaint = Pick<Complaint, "status" | "companyId" | "companyName"> & {
  kind?: ComplaintKind | null;
  urgent?: boolean;
};

export type ComplaintFilterCriteria = {
  /** "" = all statuses. */
  status: ComplaintStatus | "";
  /** companyId; "" = all companies (customer callers only ever see their own anyway). */
  companyId: string;
  /** "" = every kind, including a complaint filed before the kind existed. */
  kind: ComplaintKind | "";
  /** True narrows to the complaints that jump the queue. */
  urgentOnly: boolean;
};

export const EMPTY_COMPLAINT_FILTER: ComplaintFilterCriteria = {
  status: "",
  companyId: "",
  kind: "",
  urgentOnly: false,
};

/** AND-combines every active criterion. */
export function filterComplaints<T extends FilterableComplaint>(
  items: T[],
  criteria: ComplaintFilterCriteria,
): T[] {
  return items.filter((c) => {
    if (criteria.status && c.status !== criteria.status) return false;
    if (criteria.companyId && c.companyId !== criteria.companyId) return false;
    if (criteria.kind && c.kind !== criteria.kind) return false;
    if (criteria.urgentOnly && !c.urgent) return false;
    return true;
  });
}

/** True when at least one criterion is active — drives the "clear filters" button visibility. */
export function hasActiveComplaintFilter(criteria: ComplaintFilterCriteria): boolean {
  return (
    criteria.status !== "" ||
    criteria.companyId !== "" ||
    criteria.kind !== "" ||
    criteria.urgentOnly
  );
}

export type CompanyOption = { id: string; name: string };

/** Distinct companies (companyId + name) present in the loaded list, sorted by name.
 * Drives the internal-side "customer" filter dropdown — for a customer caller the
 * loaded list only ever contains their own company, so the page can hide the
 * dropdown entirely when this returns a single entry. */
export function distinctComplaintCompanies(items: FilterableComplaint[]): CompanyOption[] {
  const byId = new Map<string, string>();
  for (const c of items) {
    if (!byId.has(c.companyId)) byId.set(c.companyId, c.companyName);
  }
  return [...byId.entries()]
    .map(([id, name]) => ({ id, name }))
    .sort((a, b) => a.name.localeCompare(b.name));
}

export type NewComplaintFormInput = {
  title: string;
};

export type NewComplaintFormErrors = {
  title?: "required";
};

/** Mirrors the backend's `@NotBlank title` constraint (ComplaintDtos.CreateComplaintRequest): the title
 * is the one field the form insists on, because everything else about a complaint can be filled in by
 * whoever takes it over. */
export function validateNewComplaintForm(input: NewComplaintFormInput): NewComplaintFormErrors {
  const errors: NewComplaintFormErrors = {};
  if (!input.title.trim()) errors.title = "required";
  return errors;
}

/** True when the form has no validation errors — drives submit-button enablement. */
export function isNewComplaintFormValid(input: NewComplaintFormInput): boolean {
  return Object.keys(validateNewComplaintForm(input)).length === 0;
}

/** Mirrors the backend's REJECT_REASON_REQUIRED rule (ComplaintService.transition):
 * a reason is mandatory only when transitioning into REJECTED. Returns null when valid. */
export function validateTransitionReason(to: ComplaintStatus, reason: string): "reasonRequired" | null {
  if (to === "REJECTED" && !reason.trim()) return "reasonRequired";
  return null;
}

/** How a deadline stands right now. "none" is a complaint filed before the deadlines existed. */
export type DeadlineState = "none" | "met" | "due" | "missed";

/**
 * Mirrors `ComplaintDeadlines.missed`: calendar time, and an answer that arrived counts as of when it
 * arrived rather than as of now. A deadline that was never set is never late.
 */
export function deadlineState(
  dueAt: string | null,
  answeredAt: string | null,
  now: Date = new Date(),
): DeadlineState {
  if (!dueAt) return "none";
  const due = new Date(dueAt).getTime();
  if (answeredAt) return new Date(answeredAt).getTime() > due ? "missed" : "met";
  return now.getTime() > due ? "missed" : "due";
}

/** The house tag colour for a deadline, so the list and the detail page cannot disagree. */
export function deadlineTone(state: DeadlineState): "ok" | "warn" | "err" | "neutral" {
  switch (state) {
    case "met": return "ok";
    case "due": return "warn";
    case "missed": return "err";
    case "none": return "neutral";
  }
}

export type OutcomeTotal = { currency: string; amount: number; count: number };

/**
 * Totals the settled value of complaints, grouped by currency.
 *
 * <p>Amounts in different currencies are never added together in this project: without the rate on the
 * day, a sum of dinars and euros is a number that means nothing. Rows with no amount, or with an amount
 * the caller may not see, are counted nowhere.
 */
export function outcomeTotalsByCurrency(
  items: Pick<Complaint, "outcomeAmount" | "outcomeCurrency">[],
): OutcomeTotal[] {
  const byCurrency = new Map<string, OutcomeTotal>();
  for (const c of items) {
    if (!c.outcomeAmount || !c.outcomeCurrency) continue;
    const amount = Number(c.outcomeAmount);
    if (!Number.isFinite(amount)) continue;
    const current = byCurrency.get(c.outcomeCurrency) ?? { currency: c.outcomeCurrency, amount: 0, count: 0 };
    current.amount += amount;
    current.count += 1;
    byCurrency.set(c.outcomeCurrency, current);
  }
  return [...byCurrency.values()].sort((a, b) => a.currency.localeCompare(b.currency));
}

/**
 * Mirrors the backend's amount rules on a settlement: an amount only goes with an outcome that carries
 * one, and it needs a currency. Returns null when the form may be sent.
 */
export function validateOutcomeAmount(
  carriesAmount: boolean,
  amount: string,
  currency: string,
): "amountNotAllowed" | "currencyRequired" | "amountInvalid" | null {
  const trimmed = amount.trim();
  if (!trimmed) return null;
  if (!carriesAmount) return "amountNotAllowed";
  const parsed = Number(trimmed);
  if (!Number.isFinite(parsed) || parsed < 0) return "amountInvalid";
  if (!currency.trim()) return "currencyRequired";
  return null;
}

/** How far an 8D has got. Used for the progress line and to tell a finished 8D from an open one. */
export function eightDProgress(steps: { completedAt: string | null }[]): { done: number; total: number } {
  return { done: steps.filter((s) => s.completedAt !== null).length, total: steps.length };
}
